// BOORO: the decision on a task that waits for review. One component so the
// Start card and the task's chat panel offer the same three ways out.
import { Trans, useLingui } from "@lingui/react/macro";
import { Button, Textarea } from "@rakazo/ui-web";
import { useState } from "react";
import type { RpcClient } from "../lib/rpc";
import { rpc } from "../lib/rpc";
import { errorText } from "../lib/user-error";

export function TaskReviewActions({
  ticketId,
  client = rpc,
  onChanged,
  requestChanges: offerChanges = true,
}: {
  ticketId: string;
  /** The office of the task; the selected one by default. */
  client?: RpcClient;
  /** Reloads the task and the place that shows it after a decision. */
  onChanged: () => Promise<void>;
  /** Off where a composer beside the actions already sends the task back. */
  requestChanges?: boolean;
}) {
  const { t } = useLingui();
  const [requesting, setRequesting] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function approve() {
    setBusy(true);
    setError("");
    try {
      // The agent reads the comment and the queue wakes it, like a message from the sub-chat.
      await client.tickets.comment({ ticketId, body: t`Approved.` });
      await client.tickets.update({ id: ticketId, status: "todo" });
      await onChanged();
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  async function decline() {
    setBusy(true);
    setError("");
    try {
      await client.booroTasks.decline({ ticketId });
      await onChanged();
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  async function requestChanges() {
    const body = comment.trim();
    if (!body) return;
    setBusy(true);
    setError("");
    try {
      await client.tickets.comment({ ticketId, body });
      await client.tickets.update({ id: ticketId, status: "todo" });
      setComment("");
      setRequesting(false);
      await onChanged();
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="task-review-actions" className="space-y-2">
      {requesting ? (
        <>
          <Textarea
            autoFocus
            aria-label={t`What should change?`}
            placeholder={t`What should change?`}
            rows={3}
            value={comment}
            disabled={busy}
            onChange={(event) => setComment(event.target.value)}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={busy || !comment.trim()}
              onClick={() => void requestChanges()}
            >
              {busy ? <Trans>Sending…</Trans> : <Trans>Send back</Trans>}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setComment("");
                setRequesting(false);
              }}
            >
              <Trans>Cancel</Trans>
            </Button>
          </div>
        </>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={busy} onClick={() => void approve()}>
            {busy ? <Trans>Sending…</Trans> : <Trans>Approve</Trans>}
          </Button>
          {offerChanges ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setRequesting(true)}>
              <Trans>Request changes</Trans>
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void decline()}>
            <Trans>Decline</Trans>
          </Button>
        </div>
      )}
      {error ? (
        <p role="alert" className="text-[13px] text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
